<script setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useResizeObserver } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ScheduledMessageDialog from './ScheduledMessageDialog.vue';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useLocale } from 'shared/composables/useLocale';

const { t } = useI18n();
const { resolvedLocale } = useLocale();
const { isEnterprise } = useConfig();
const { isAWhatsAppCloudChannel } = useInbox();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const managementDialogRef = ref(null);
const cancelDialogRef = ref(null);
const previewRef = useTemplateRef('previewRef');
const isCancelling = ref(false);
const isPreviewExpanded = ref(false);
const canExpandPreview = ref(false);

const scheduledMessage = computed(
  () => currentChat.value?.scheduled_message || null
);
const shouldLoad = computed(
  () => isEnterprise && isAWhatsAppCloudChannel.value
);
const shouldShow = computed(
  () =>
    shouldLoad.value &&
    ['pending', 'processing'].includes(scheduledMessage.value?.status)
);
const canManage = computed(
  () =>
    scheduledMessage.value?.status === 'pending' &&
    currentChat.value?.can_manage_scheduled_message
);
const previewToggleLabel = computed(() =>
  isPreviewExpanded.value
    ? t('CONVERSATION.SCHEDULED_MESSAGE.SHOW_LESS')
    : t('CONVERSATION.SCHEDULED_MESSAGE.SHOW_MORE')
);
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const formattedScheduledAt = computed(() => {
  if (!scheduledMessage.value?.scheduled_at) return '';

  return new Intl.DateTimeFormat(resolvedLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(new Date(scheduledMessage.value.scheduled_at));
});
const openManagementDialog = () => managementDialogRef.value?.open();
const openCancelDialog = () => cancelDialogRef.value?.open();

const updatePreviewOverflow = () => {
  if (!previewRef.value || isPreviewExpanded.value) return;

  canExpandPreview.value =
    previewRef.value.scrollHeight > previewRef.value.clientHeight + 1;
};

const resetPreview = async () => {
  isPreviewExpanded.value = false;
  canExpandPreview.value = false;
  await nextTick();
  updatePreviewOverflow();
};

const togglePreview = async () => {
  isPreviewExpanded.value = !isPreviewExpanded.value;
  if (!isPreviewExpanded.value) {
    await nextTick();
    updatePreviewOverflow();
  }
};

useResizeObserver(previewRef, updatePreviewOverflow);

const cancelScheduledMessage = async () => {
  const pending = scheduledMessage.value;
  if (!pending || isCancelling.value) return;

  isCancelling.value = true;
  try {
    await store.dispatch('cancelScheduledMessage', {
      conversationId: currentChat.value.id,
      scheduledMessageId: pending.id,
      lockVersion: pending.lock_version,
    });
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL_SUCCESS'));
    cancelDialogRef.value?.close();
  } catch (error) {
    cancelDialogRef.value?.close();
    useAlert(
      error.response?.status === 409
        ? t('CONVERSATION.SCHEDULED_MESSAGE.CONFLICT')
        : error.response?.data?.error ||
            t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL_ERROR')
    );
  } finally {
    isCancelling.value = false;
  }
};

watch(
  [() => currentChat.value?.id, shouldLoad],
  ([conversationId, canLoad]) => {
    if (conversationId && canLoad) {
      store.dispatch('fetchScheduledMessage', conversationId).catch(() => {});
    }
  },
  { immediate: true }
);

watch(() => scheduledMessage.value?.preview, resetPreview, { immediate: true });
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <li v-if="shouldShow" class="list-none flex justify-end w-full mb-2">
    <section
      role="status"
      aria-live="polite"
      :data-scheduled-message-id="scheduledMessage.id"
      class="min-w-0 max-w-lg ltr:ml-8 rtl:mr-8 px-4 py-3 rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm border-2 border-dashed border-n-amber-10 bg-n-amber-4 text-n-slate-12"
    >
      <p class="mb-1 text-xs font-medium text-n-slate-11">
        {{ t('CONVERSATION.SCHEDULED_MESSAGE.BUBBLE_TITLE') }}
      </p>
      <p
        :id="`scheduled-message-preview-${scheduledMessage.id}`"
        ref="previewRef"
        class="mb-0 text-sm leading-relaxed whitespace-pre-wrap break-words"
        :class="{ 'line-clamp-4': !isPreviewExpanded }"
      >
        {{ scheduledMessage.preview }}
      </p>
      <Button
        v-if="canExpandPreview"
        :aria-controls="`scheduled-message-preview-${scheduledMessage.id}`"
        :aria-expanded="isPreviewExpanded"
        :label="previewToggleLabel"
        variant="link"
        color="slate"
        size="xs"
        class="mt-1"
        @click="togglePreview"
      />
      <p
        v-if="scheduledMessage.customer_replied_since_last_change"
        data-testid="scheduled-message-reply-warning"
        class="flex items-center gap-1.5 mt-2 mb-0 px-2 py-1.5 rounded-md text-xs font-medium bg-n-amber-6 text-n-amber-12"
      >
        <span class="i-ph-chat-circle-dots size-3.5 flex-shrink-0" />
        {{ t('CONVERSATION.SCHEDULED_MESSAGE.CUSTOMER_REPLIED') }}
      </p>
      <div
        class="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-dashed border-n-amber-7"
      >
        <span class="flex items-center min-w-0 gap-1.5 text-xs text-n-slate-11">
          <span
            data-testid="scheduled-message-lock"
            class="i-ph-lock size-3.5 text-n-amber-11"
          />
          <span class="truncate">
            {{
              t('CONVERSATION.SCHEDULED_MESSAGE.SCHEDULED_FOR', {
                time: formattedScheduledAt,
              })
            }}
          </span>
        </span>
        <div v-if="canManage" class="flex flex-shrink-0 gap-1">
          <Button
            v-tooltip.top="t('CONVERSATION.SCHEDULED_MESSAGE.EDIT')"
            data-testid="scheduled-message-edit"
            :aria-label="t('CONVERSATION.SCHEDULED_MESSAGE.EDIT')"
            icon="i-ph-pencil-simple"
            slate
            ghost
            xs
            @click="openManagementDialog"
          />
          <Button
            v-tooltip.top="t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL')"
            data-testid="scheduled-message-cancel"
            :aria-label="t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL')"
            icon="i-ph-trash"
            ruby
            ghost
            xs
            @click="openCancelDialog"
          />
        </div>
      </div>
    </section>

    <ScheduledMessageDialog ref="managementDialogRef" />
    <Dialog
      ref="cancelDialogRef"
      type="alert"
      :title="t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL_TITLE')"
      :description="t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL_DESCRIPTION')"
      :confirm-button-label="t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL_CONFIRM')"
      :is-loading="isCancelling"
      width="sm"
      @confirm="cancelScheduledMessage"
    />
  </li>
</template>
